// Portal API Service
// All API calls for the customer portal
import { getApiBaseUrl } from '../../config/api';

// Lazy so it reflects the backend resolved by initApiBase() on previews —
// a module-level const would capture the sandbox fallback at import time.
const apiBase = () => `${getApiBaseUrl()}/portal`;

const getAuthHeader = (): HeadersInit => {
  const token = localStorage.getItem('portal_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json();
};

// Sync
export const syncOrders = async () => {
  const response = await fetch(`${apiBase()}/sync`, {
    method: 'POST',
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Dashboard
export const fetchDashboard = async () => {
  const response = await fetch(`${apiBase()}/dashboard`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Orders
export const fetchOrders = async (params?: { status?: string; page?: number; limit?: number }) => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const response = await fetch(`${apiBase()}/orders?${searchParams}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const fetchOrder = async (orderId: string) => {
  const response = await fetch(`${apiBase()}/orders/${orderId}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Tracking
export const fetchTracking = async (orderId: string) => {
  const response = await fetch(`${apiBase()}/tracking/${orderId}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Documents
export const fetchDocuments = async (params?: { type?: string; page?: number; limit?: number }) => {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const response = await fetch(`${apiBase()}/documents?${searchParams}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const fetchDocument = async (documentId: string) => {
  const response = await fetch(`${apiBase()}/documents/${documentId}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const fetchDocumentPdf = async (documentId: string): Promise<Blob> => {
  const response = await fetch(`${apiBase()}/documents/${documentId}/pdf`, {
    headers: { ...getAuthHeader() },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Download failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.blob();
};

// Messages
export const fetchConversations = async () => {
  const response = await fetch(`${apiBase()}/messages/conversations`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const fetchMessages = async (conversationId: string) => {
  const response = await fetch(`${apiBase()}/messages/${conversationId}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const sendMessage = async (conversationId: string, text: string) => {
  const response = await fetch(`${apiBase()}/messages/${conversationId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ text }),
  });
  return handleResponse(response);
};

export const createConversation = async (subject: string, message: string) => {
  const response = await fetch(`${apiBase()}/messages/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ subject, message }),
  });
  return handleResponse(response);
};

// Profile
export const fetchProfile = async () => {
  const response = await fetch(`${apiBase()}/profile`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const updateProfile = async (data: { name?: string; phone?: string; company?: string }) => {
  const response = await fetch(`${apiBase()}/profile`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

// Addresses
export const fetchAddresses = async () => {
  const response = await fetch(`${apiBase()}/profile/addresses`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

export const addAddress = async (address: {
  label?: string;
  address1: string;
  address2?: string;
  city: string;
  region: string;
  zipcode: string;
  country: string;
  isPrimary?: boolean;
}) => {
  const response = await fetch(`${apiBase()}/profile/addresses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(address),
  });
  return handleResponse(response);
};

export const updateAddress = async (addressId: string, address: {
  label?: string;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  zipcode?: string;
  country?: string;
  isPrimary?: boolean;
}) => {
  const response = await fetch(`${apiBase()}/profile/addresses/${addressId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(address),
  });
  return handleResponse(response);
};

export const deleteAddress = async (addressId: string) => {
  const response = await fetch(`${apiBase()}/profile/addresses/${addressId}`, {
    method: 'DELETE',
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Activity Log
export const fetchActivityLog = async (params?: { page?: number; limit?: number }) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const response = await fetch(`${apiBase()}/activity?${searchParams}`, {
    headers: { ...getAuthHeader() },
  });
  return handleResponse(response);
};

// Password
export const changePassword = async (currentPassword: string, newPassword: string) => {
  const response = await fetch(`${apiBase()}/profile/password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return handleResponse(response);
};
